import { useContext, type ReactNode } from "react"
import { rootContext } from "../../contexts/rootContext"
import KayakingIcon from '@mui/icons-material/KayakingRounded'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import LegendToggleIcon from '@mui/icons-material/LegendToggle'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined'
import NavigationIcon from '@mui/icons-material/Navigation'
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation'
import { primitiveAccessPointColor } from "../../utilities/buffaloArcGisHelper"

const resources = [
    { label: "NPS - Buffalo National River", href: "https://www.nps.gov/buff/index.htm" },
    { label: "NPS - Float Guide", href: "https://nps.maps.arcgis.com/apps/dashboards/3606e2401fdd428cbbdff9518cbe11af" },
    { label: "USGS - Buffalo River Map", href: "https://apps.usgs.gov/lmg/buffaloriver/" },
]

export const AboutMap = () => {
    const context = useContext(rootContext)
    const { text } = context.theme

    const Section = (props: { title: string, children: ReactNode }) => (
        <div style={{ marginBottom: "20px" }}>
            <div style={{
                color: text.contrast5,
                fontSize: "18px",
                fontWeight: "bold",
                paddingBottom: "4px",
                marginBottom: "8px",
                borderBottom: `1px solid ${text.contrast5}`
            }}>
                {props.title}
            </div>
            {props.children}
        </div>
    )

    // One row per feature: accent colored icon, bold name, dimmer detail.
    const Row = (props: { icon: ReactNode, name: string, detail?: string, indent?: boolean }) => (
        <div style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            padding: "4px 0",
            marginLeft: props.indent ? "28px" : 0
        }}>
            <span style={{ display: "flex", flexShrink: 0, paddingTop: "1px" }}>{props.icon}</span>
            <span>
                <span style={{ color: text.primary, fontWeight: "bold" }}>{props.name}</span>
                {props.detail !== undefined &&
                    <span style={{ color: text.primary, opacity: 0.75 }}> &ndash; {props.detail}</span>
                }
            </span>
        </div>
    )

    const icon = (Icon: typeof KayakingIcon, color: string = text.contrast4) =>
        <Icon sx={{ color, fontSize: "22px" }} />

    return (<>
        <Section title="Water Level">
            <Row
                icon={icon(LegendToggleIcon)}
                name="Estimated, not measured"
                detail="river color is interpolated from USGS sensors."
            />
            <Row
                icon={icon(AccessTimeIcon)}
                name="Reload for fresh data"
                detail="readings load once, when the page opens."
            />
        </Section>
        <Section title="Map Controls">
            <Row icon={icon(NavigationIcon)} name="Compass" detail="reset to north." />
            <Row icon={icon(KayakingIcon)} name="Access points" detail="put-ins and take-outs." />
            <Row
                icon={icon(KayakingIcon, primitiveAccessPointColor)}
                name="Primitive"
                detail="rough road, no facilities."
                indent
            />
            <Row icon={icon(VisibilityOutlinedIcon)} name="Points of interest" detail="pick which kinds to show, then click one to learn more." />
            <Row icon={icon(LegendToggleIcon)} name="Legend" />
            <Row icon={icon(AccessTimeIcon)} name="Time selector" detail="replay the past 48 hours." />
            <Row icon={icon(SatelliteAltOutlinedIcon)} name="Satellite imagery" />
            <Row
                icon={icon(ThreeDRotationIcon)}
                name="3D tilt"
                detail="right click and drag, or two finger drag on touch screens."
            />
        </Section>
        <Section title="Plan a Trip">
            <Row
                icon={<span style={{ color: text.contrast3, fontWeight: "bold", width: "22px", textAlign: "center" }}>1</span>}
                name="Click two access points"
                detail="see distance and estimated time."
            />
            <Row
                icon={<span style={{ color: text.contrast3, fontWeight: "bold", width: "22px", textAlign: "center" }}>2</span>}
                name="Open trip details"
                detail="daylight, directions, and a shareable link."
            />
        </Section>
        <Section title="Resources">
            {resources.map(resource =>
                <div key={resource.href} style={{ padding: "4px 0" }}>
                    <a
                        style={{
                            color: text.contrast4,
                            cursor: `url(${context.theme.cursor.pointer}), default`
                        }}
                        href={resource.href}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {resource.label}
                    </a>
                </div>
            )}
        </Section>
    </>)
}
